// Builds the Typesense `sort_by` string for the web search dialog (the
// `searchable` collection).
//
// Ranking: text relevance → soft distance bands (when location is known) →
// popularity.
//
// We deliberately use coarse `_eval` distance tiers rather than a continuous
// `location():asc` sort. A continuous geo sort never ties, so it starves the
// popularity tiebreaker entirely — nearest always wins and popularity never
// gets a vote. Broad bands let popularity do the real ordering *within* each
// tier, so a much more popular hike a bit further away can still outrank a
// mediocre nearer one, while genuinely local results still cluster to the top.
// Band breadth is the weighting dial: wider bands → softer distance influence.
//
// Location is optional (IP-derived, best-effort). When it is absent we drop the
// geo term and fall back to text relevance → popularity.

export interface GeoPoint {
  lat: number;
  lng: number;
}

// Distance band radius (km) → `_eval` weight. Inner (nearer) bands carry the
// higher weight so closer hikes always score at least as high as further ones.
export const SEARCH_DISTANCE_BANDS: ReadonlyArray<{ km: number; weight: number }> = [
  { km: 20, weight: 3 },
  { km: 50, weight: 2 },
];

export function buildSearchableSortBy(geo?: GeoPoint | null): string {
  if (!geo) {
    return '_text_match:desc,popularity:desc';
  }

  const bands = SEARCH_DISTANCE_BANDS
    .map((band) => `(location:(${geo.lat}, ${geo.lng}, ${band.km} km)):${band.weight}`)
    .join(',');

  return `_text_match:desc,_eval([${bands}]):desc,popularity:desc`;
}
